
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useTransition,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { hasNonEmptyConditions } from "@/components/ConditionBuilder/condition-utils";
import { mergeFlatIntoGroup } from "@/lib/utils/condition-converters";
import { Guest } from "../types/Guest";
import { AcceptanceState } from "../types/enums/AcceptanceState";
import { GuestState } from "../types/enums/GuestState";
import {
  approvalBlockedGuestIds,
  isCapacityDecision,
  isServerErr,
  isServerErrorResponse,
  capacityContextOf,
  CAPACITY_DECISION_PENDING,
  CapacityContext,
  SaveErrorPayload,
  throwServerError,
  isBackendErrorCode,
} from "@/lib/api/errors/ErrorHandler";
import { CapacityDecisionModal } from "@/components/CapacityDecisionModal/CapacityDecisionModal";
import { BulkSelection, BulkEditableUpdates } from "../types/BulkSelection";
import type { DeleteGuestsResult } from "@/types/GuestTrash";
import { useFilters } from "./FiltersProvider";
import { useMetaTable } from "./MetaTableProvider";
import {
  getGuests,
  archiveGuests as archiveGuestsApi,
  unarchiveGuests as unarchiveGuestsApi,
  deleteGuests as deleteGuestsApi,
  bulkEditGuests as bulkEditGuestsApi,
  checkInGuest as checkInGuestApi,
  updateGuests,
  createGuests,
  findDuplicatesByField,
} from "@/lib/api/handlers/GuestHandling";
import {
  runPartyCheckIn,
  type PartyCheckInReport,
} from "@/lib/guests/checkInParty";
import { qk } from "@/lib/query/queryKeys";
import { invalidateAfterCheckIn } from "@/lib/guests/invalidateAfterCheckIn";
import { patchGuestInRequestsCache } from "@/lib/query/requestsCache";
import { mergeGuestPatch } from "@/lib/utils/guest-patch";
import { findGuestById, flattenGuestTree, mapGuestTree } from "@/lib/guests/partyTree";
import { useAiDataSync } from "@/lib/ai-sync/useAiDataSync";
import { useSignatureEffect } from "../hooks/useSignatureEffect";
import {
  parseFiltersApiRequest,
  parseGuestsSpecial,
  parseSortByApiRequest,
  pruneStaleAdditionalFields,
} from "@/lib/utils/parser";
import { Field, isComputedField } from "../types/Field";
import { useFields } from "./FieldsProvider";
import {
  armGuestReconcile,
  armCoarseReconcile,
  FieldWatch,
} from "@/lib/guest-reconcile/reconcileScheduler";
import {
  deriveLifecycleTriggers,
  explicitUpdateTrigger,
} from "@/lib/utils/derive-lifecycle-triggers";
import { useGuestAutomationGate } from "@/hooks/useGuestAutomationGate";
import {
  isGroupByRejection,
  isPartyGroup,
  nextGroupByRejection,
  suspendGroupByForSession,
  type GroupByRejection,
  type GroupByRejectionReason,
} from "@/lib/guests/groupBy";
import { useTranslations } from "next-intl";
import { PConfirmDialog } from "@/components/PConfirmDialog/PConfirmDialog";

import { EmailTrigger } from "@/types/enums/EmailTrigger";

/**
 * One guest whose pending edit is about to give up their seat(s).
 *
 * `reason` distinguishes the two writes that trigger the server-side release
 * so the dialog can say which one it is — "declining" and "archiving" are very
 * different intentions and an organiser doing one should not read the other.
 */
type SeatReleaseWarning = {
  guestId: string;
  name: string;
  seatCount: number;
  reason: "declined" | "archived";
};

/**
 * The seat-release confirmation, as its OWN component rather than inline JSX.
 *
 * `useTranslations` throws without a NextIntlClientProvider above it. Calling it
 * in GuestsProvider itself would make every consumer of the guests context —
 * including a dozen unit tests that mount the provider bare to exercise
 * unrelated save paths — depend on an i18n provider they have no reason to set
 * up. Here the hook only runs when a release is actually about to happen.
 */
const SeatReleaseConfirm = ({
  warnings,
  isSaving,
  onConfirm,
  onCancel,
}: {
  warnings: SeatReleaseWarning[];
  isSaving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const t = useTranslations("guestlist");
  const NAMED_LIMIT = 12;
  return (
    <PConfirmDialog
      title={t("seatReleaseTitle")}
      message={t("seatReleaseMessage", {
        count: warnings.length,
        seats: warnings.reduce((sum, w) => sum + w.seatCount, 0),
      })}
      confirmText={t("seatReleaseConfirm")}
      isLoading={isSaving}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {/* Names, so the organiser can recognise the arrangement they are about
          to lose. Capped — a bulk edit can carry hundreds, and a dialog that
          scrolls its own buttons off the screen is a dialog nobody reads. */}
      <ul className="body-3-regular font-secondary list-disc pl-5 space-y-0.5 max-h-40 overflow-y-auto">
        {warnings.slice(0, NAMED_LIMIT).map((w) => (
          <li key={w.guestId}>
            {w.name || w.guestId}
            {" — "}
            {t("seatReleaseSeatCount", { count: w.seatCount })}
          </li>
        ))}
      </ul>
      {warnings.length > NAMED_LIMIT && (
        <p className="body-3-regular font-tertiary mt-1">
          {t("seatReleaseAndMore", { count: warnings.length - NAMED_LIMIT })}
        </p>
      )}
    </PConfirmDialog>
  );
};

/**
 * Detail of the `guestDataUpdated` window event dispatched by
 * {@link GuestsProvider}'s `updateGuest` after a confirmed field save.
 * RegistrantsProvider/CompanionsProvider merge `patch` into matching rows
 * (see `mergeGuestPatch`) so a popup edit on the requests page reaches the
 * table without a reload. Acceptance transitions are NOT carried here —
 * `guestAcceptanceResolved` owns those (row drop + counts + refetch shield).
 */
export type GuestDataUpdatedDetail = {
  eventId: string;
  guestId: string;
  patch: Partial<Guest>;
};

/**
 * Detail of the `guestsCreated` window event {@link GuestsProvider}'s
 * `performCreate` dispatches after a confirmed create.
 *
 * Exists because the create door is not the only surface holding guests:
 * `GuestCompanions` keeps a per-host companion list that it fetches ONCE, and
 * the guest-detail popup reads it. Before this event, that list was only ever
 * appended to by the one call site that happened to know about it — so a
 * companion created through the capacity popup (a different call site, two
 * dialogs away) simply never appeared, and the placeholder numbering, which
 * reads that same list, restarted at 1 and minted duplicates.
 */
export type GuestsCreatedDetail = {
  eventId: string;
  guests: Guest[];
};

interface GuestsContextProps {
  duplicateGuest: (ids: string) => Promise<void>;
  archiveGuests: (selection: BulkSelection) => Promise<void>;
  unarchiveGuests: (selection: BulkSelection) => Promise<void>;
  bulkEditGuests: (selection: BulkSelection, updates: BulkEditableUpdates) => Promise<void>;
  addGuest: (guest: Guest) => Promise<Guest>;
  /** Same create door as `addGuest`, one request for the whole list. Used by
   *  the "N placeholder companions" quick-add; the capacity popup, if it
   *  opens, decides for the WHOLE batch (the backend rejects it as one). */
  addGuests: (guests: Guest[]) => Promise<Guest[]>;
  /**
   * W6: answers the door's `{mode, count, trashBatchId, purgeAt}` (null from a
   * backend without the trash) so the caller can arm the 15 s undo;
   * `permanent: true` skips the trash.
   */
  deleteGuests: (selection: BulkSelection, options?: { permanent?: boolean }) => Promise<DeleteGuestsResult | null>;
  buildUpdateGuests: (
    guestId: string,
    field: Field,
    newValue: string | string[]
  ) => void;
  saveUpdateGuests: () => Promise<void>;
  updateGuest: (
    newGuest: Guest,
    avoidUpdate: boolean,
    prevMainGuestId: string,
    currentMainGuestId: string
  ) => Promise<void>;
  checkInGuest: (guestId: string, performedByType?: string) => Promise<{ warning?: string } | void>;
  /**
   * Check a whole party in or out — the guest-list row action for a guest who
   * has companions.
   *
   * Deliberately NOT `checkInGuest` in a loop: that one derives its direction
   * from the row it finds in `guests`, so a companion who is already inside
   * would be checked OUT, and a companion the party-grouped list keeps nested
   * (not a top-level row) resolves to "not checked in" and is always checked
   * IN. A party has ONE direction, the one the operator clicked, and it travels
   * explicitly. See `runPartyCheckIn` for why this is sequential.
   */
  checkInGuests: (
    guestIds: string[],
    checkIn: boolean,
    performedByType?: string,
  ) => Promise<PartyCheckInReport>;
  /** Silent refetch of the current page/filters (no table flash). Used to pull
   *  in async server-side mutations like workflow UPDATE_GUEST results. */
  refreshGuests: () => void;
  /** The event this guest list belongs to — the reliable eventId source for the
   *  detail popup, which sits outside EventProvider and whose guest rows don't
   *  always carry eventId. */
  eventId: string;
  discardPendingUpdates: () => void;
  hasPendingChanges: boolean;
  /** PB-TI-2: last inline-save failure, or null. Surfaced by the editing
   *  banner, which translates it via `describeBatchUpdateError`. Structured
   *  rather than a string so the per-guest `failures[]` reasons survive to the
   *  render site — the batch 400 has no top-level message to fall back on. */
  saveError: SaveErrorPayload | string | null;
  /** PB-TI-2: true while an inline save is in flight. */
  isSavingGuests: boolean;
  dismissSaveError: () => void;
  guests: Guest[];
  /** PB-TJ-3: the active filter names a field the event no longer has, so the
   *  list is deliberately EMPTY rather than silently un-filtered. */
  brokenFilterKey: boolean;
  /** Set when the backend refused the active grouping; the grouping is off. */
  groupByRejected: GroupByRejection;
  /**
   * Set when the duplicates fetch failed (400/403/5xx); duplicate mode is
   * already off by then. `seq` gives a repeat of the same code its own
   * identity so the toast in GuestsCP fires again (see GroupByRejection).
   */
  duplicateRejected: DuplicateRejection;
  loading: boolean;
}

/**
 * Why the last duplicates request failed, or null. Same shape idea as
 * {@link GroupByRejection}: the code is what the backend said
 * (`duplicateFieldForbidden`, `duplicateFieldInvalid`, …) and `detail` its
 * optional suffix; both feed translateBackendError in the toast.
 */
export type DuplicateRejection = { code: string; detail?: string; seq: number } | null;

/**
 * Is this duplicates failure a REFUSAL (the request will be refused again for
 * the same keys) rather than a transient failure? Only a refusal ends
 * duplicate mode; everything else is announced and retried on the next poll.
 */
function isDuplicateRefusal(code: string): boolean {
  return (
    code.startsWith("duplicateField") ||
    code === "validationError" ||
    code === "pageSizeExceeded" ||
    code === "noAccessToResource" ||
    code === "insufficientCapability" ||
    code === "eventArchived"
  );
}

/** Read one field off a guest, standard column vs nested additionalFields. */
function readGuestField(
  guest: Guest,
  field: string,
  isAdditional: boolean,
): unknown {
  if (isAdditional) {
    const af = (guest as { additionalFields?: Record<string, unknown> })
      .additionalFields;
    return af ? af[field] : undefined;
  }
  return (guest as unknown as Record<string, unknown>)[field];
}

export const GuestsContext = createContext<GuestsContextProps | undefined>(undefined);

interface GuestsProviderProps {
  children: ReactNode;
  initialGuests: Guest[];
  eventId: string;
  baseFilters?: { key: string; operator: string; value: string }[];
  /** PB-TJ-3: the host's own first-paint query already hit `unknownFilterKey`.
   *  Seed the flag so the banner shows on first paint — the provider dedupes
   *  its mount fetch against that same request and would never learn it. */
  initialBrokenFilterKey?: boolean;
  /**
   * A grouping the HOST already had refused, before this provider existed.
   *
   * The page prefetches the first grouped page itself, so a refusal is seen
   * there — and the page resets the grouping before mounting this tree, which
   * means the provider never issues the request that would set the flag. The
   * reason has to be handed over, or every page-load refusal is silent while
   * every in-session one is announced.
   */
  initialGroupByRejection?: GroupByRejectionReason | null;
}

export const GuestsProvider = ({
  initialGuests,
  children,
  eventId,
  baseFilters,
  initialBrokenFilterKey = false,
  initialGroupByRejection = null,
}: GuestsProviderProps) => {
  const { allFieldsUnfiltered } = useFields();

  /**
   * Custom fields a client may actually WRITE.
   *
   * Excludes computed columns for the same reason the batch lane excludes
   * since-deleted ones: the server refuses the key and fails the whole batch,
   * and the value is re-derived after every write anyway.
   */
  const writableCustomFieldNames = useMemo(
    () =>
      new Set(
        allFieldsUnfiltered
          .filter((f) => f.additionalField && !isComputedField(f))
          .map((f) => f.name),
      ),
    [allFieldsUnfiltered],
  );
  const { activeFilterState } = useFilters();
  const {
    archived,
    sortBy,
    sortOrder,
    page,
    pageSize,
    search,
    setTotalCount,
    refreshCounts,
    setPage,
    isDuplicateMode,
    duplicateFields,
    setDuplicateMode,
    groupBy,
    setGroupBy,
    suspendGroupBy,
  } = useMetaTable();

  const [isPending, startTransition] = useTransition();
  const [guests, setGuests] = useState<Guest[]>(initialGuests);
  const pendingUpdatesRef = useRef<Guest[]>([]);
  // Which draft entries are the RESTORED pre-override payload rather than
  // something the admin typed. The two are indistinguishable by shape —
  // `parseGuestsSpecial` copies the guest's whole additionalFields bag into
  // every entry — so provenance has to be recorded when it is still known.
  const restoredDraftIdsRef = useRef<Set<string>>(new Set());
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  // PB-TI-2: a failed inline save used to be console.error only — the cell
  // snapped back and the operator had no way to tell a rejected save from a
  // no-op. Track the failure and the in-flight state so the banner can say so.
  const [saveError, setSaveError] = useState<SaveErrorPayload | string | null>(
    null,
  );
  const [isSavingGuests, setIsSavingGuests] = useState(false);
  // PB-TJ-3: the active filter names a field this event no longer has. Set from
  // the backend's `unknownFilterKey` 400 — the ONE fetch failure that is not
  // transient and must NOT leave the previous (wider) rows on screen.
  const [brokenFilterKey, setBrokenFilterKey] = useState(initialBrokenFilterKey);
  /**
   * Why the last grouped request was refused, or null. Drives the hint in the
   * toolbar; the grouping itself has already been dropped by then.
   */
  const [groupByRejected, setGroupByRejected] = useState<GroupByRejection>(
    initialGroupByRejection ? { reason: initialGroupByRejection, seq: 1 } : null,
  );
  const [duplicateRejected, setDuplicateRejected] = useState<DuplicateRejection>(null);
  // Unified 3-option capacity decision (shared CapacityDecisionModal):
  //  - kind "create": event full on "+ Gast" → Waitlist / Add over cap / Cancel.
  //  - kind "promote": admin tried to promote a waitlisted guest while full →
  //    Add over cap / Cancel (no waitlist choice — they're already waitlisted).
  const [capacityDecision, setCapacityDecision] = useState<{
    guests: Guest[];
    context?: CapacityContext;
    kind: "create" | "promote";
  } | null>(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  // The RAW failure of the last popup choice (M-10). This provider has no
  // translator on purpose (R12) — the popup translates it, and stays open.
  // Reset in ONE place, where the popup opens (create + promote below).
  const [decisionError, setDecisionError] = useState<unknown>(null);
  const guestsRef = useRef<Guest[]>(initialGuests);
  useEffect(() => { guestsRef.current = guests; }, [guests]);

  // Imperative refetch of the current page/filters. The actual fetch lives in
  // `runFetchRef.current`, (re)assigned every render below so it always closes
  // over the latest filters/sort/page/search. It is invoked two ways:
  //   - the navigation effect wraps it in startTransition → `isPending` true →
  //     the table dims behind a spinner. Legit: the whole dataset is changing
  //     (filter/sort/page/search/archived/duplicate-mode).
  //   - refreshGuests() below calls it WITHOUT a transition → a SILENT reconcile
  //     after a mutation. The rows update in place; the table never blanks.
  const runFetchRef = useRef<() => Promise<void>>(async () => {});

  // Monotonic issue token — ONLY the newest in-flight request may write rows.
  // Two lanes fire this fetch concurrently and neither cancels the other: the
  // signature effect (filter/sort/page/SEARCH change) and refreshGuests() (the
  // 45s poll, window focus, reconcile + post-mutation reconciles). A poll that
  // left with the OLD search and lands AFTER the search response overwrote the
  // narrowed rows with the unfiltered page — search box still showing the term,
  // count still showing the narrowed total. Intermittent by construction (it
  // needs the responses to cross), which is exactly how it was reported.
  const fetchTokenRef = useRef(0);

  // Silent reconcile used by every post-mutation refresh (create / duplicate /
  // bulk-edit / page-backfill / background-job + AI live-sync listeners). It
  // deliberately does NOT raise the table's loading overlay — optimistic state
  // is already on screen and a single action must not flash the whole table.
  const refreshGuests = useCallback(() => {
    void runFetchRef.current();
  }, []);

  // Invalidate cached spot-count queries so the "guest spots remaining"
  // banner in GuestList reflects backend changes without a page reload.
  // Also busts the event list / detail caches because the row-level
  // EventRowDTO carries per-event guest counters (guestsUncontacted,
  // guestsContacted, guestsAccepted, …) that move whenever a guest's
  // state changes — same writes drive both the banner and the grid.
  // Reconcile when the HOST's query delivers fresher data (post-invalidation
  // remount refetch): the signature refetch keys on request params — identical
  // params ⇒ no fetch — so the stale seed would otherwise pin until the next
  // poll. refreshGuests is the guarded silent reconcile (skips while inline
  // edits are pending), so reuse it instead of overwriting state directly.
  // Compared by CONTENT, not identity — identity churn on a content-equal
  // list must not refire a byte-identical fetch (fetch-dedupe pins).
  const initialSeedRef = useRef(JSON.stringify(initialGuests));
  useEffect(() => {
    const seedSignature = JSON.stringify(initialGuests);
    if (initialSeedRef.current === seedSignature) return;
    initialSeedRef.current = seedSignature;
    refreshGuests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGuests]);

  const queryClient = useQueryClient();
  // Cheap gate: no ACTIONS plugin, or no UPDATE_GUEST pipe on the triggers this
  // write fires, means no reconcile wave is started at all.
  const { mayRun, watchedFieldsFor } = useGuestAutomationGate(eventId);

  // The key list lives in `invalidateAfterCheckIn` now. It used to live only
  // here — which meant the three surfaces that mount this provider were the
  // only ones that could mark those caches stale, and a check-in written from
  // the check-in history page left the guest list showing the old rows.
  const invalidateContingent = useCallback(() => {
    invalidateAfterCheckIn(queryClient, eventId);
  }, [queryClient, eventId]);

  // Auto-refresh when a background import, bulk-edit, OR bulk-email
  // send job completes. PBulkEdit's submit closes the modal
  // immediately and the actual write lands later via the
  // BackgroundJobsProvider polling; SEND_EMAILS additionally flips
  // guest.state UNCONTACTED → CONTACTED in the consumer worker so
  // the status column needs a refresh too. This listener pulls the
  // fresh state in without forcing the user to reload.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.eventId === eventId) {
        refreshGuests();
        invalidateContingent();
      }
    };
    window.addEventListener("importGuestsCompleted", handler);
    window.addEventListener("bulkEditGuestsCompleted", handler);
    window.addEventListener("sendEmailsCompleted", handler);
    // A manual SMS send marks its guests contacted too (SMS manual send, F-3).
    window.addEventListener("sendSmsCompleted", handler);
    return () => {
      window.removeEventListener("importGuestsCompleted", handler);
      window.removeEventListener("bulkEditGuestsCompleted", handler);
      window.removeEventListener("sendEmailsCompleted", handler);
      window.removeEventListener("sendSmsCompleted", handler);
    };
  }, [refreshGuests, invalidateContingent, eventId]);

  // Same live-refresh, but for synchronous AI ("Velo") server tools that mutate
  // guests directly (create/update/delete/bulk-by-filter/check-in/assign-seat/…).
  // Reuses the exact refresh path the background-job events above use.
  useAiDataSync("guests", eventId, () => {
    refreshGuests();
    invalidateContingent();
  });

  /**
   * Arm a reconcile for the guests a batch write just touched.
   *
   * The watch is `state`/`acceptanceState` moving away from what we currently
   * render — never a predicted value. `state` is decided server-side (the
   * capacity CAS can refuse it outright), so a prediction could show a guest as
   * admitted who was not.
   */
  const armReconcileForUpdates = useCallback(
    (updates: Array<Partial<Guest>>, previous: Guest[]) => {
      if (updates.length === 0) return;
      const triggersFired = new Set<EmailTrigger>();
      const watches = new Map<string, FieldWatch[]>();
      const perGuest = new Map<
        string,
        { before: Guest | undefined; update: Partial<Guest> }
      >();
      for (const update of updates) {
        const guestId = update.guestId as string | undefined;
        if (!guestId) continue;
        // Through the party tree: on the grouped list a companion is nested
        // under its head, and a flat `find` handed `deriveLifecycleTriggers` a
        // null baseline — no triggers, no watch, no reconcile for a Status
        // edit on a companion row.
        const before = findGuestById(previous, guestId);
        const explicit = explicitUpdateTrigger(update as { archived?: boolean | null });
        if (explicit) triggersFired.add(explicit);
        for (const t of deriveLifecycleTriggers(before ?? null, update as never)) {
          triggersFired.add(t);
        }
        perGuest.set(guestId, { before, update });
      }
      if (perGuest.size === 0) return;
      const fired = [...triggersFired];
      if (!mayRun(fired)) return;
      // Watch the fields the RULES target, not a fixed pair — a rule that
      // writes `lastName` would never satisfy a state/acceptanceState watch,
      // and a watch that cannot satisfy is the same as no reconcile at all.
      const fields = watchedFieldsFor(fired);
      if (fields.length === 0) {
        // `mayRun` said "maybe" but we have no field list — the workflows query
        // has not landed yet (first mutation after a page load). A watch-less
        // per-guest wave could never terminate early, so degrade to a couple of
        // coarse pulses instead of burning the full deadline.
        armCoarseReconcile(eventId);
        return;
      }
      for (const [guestId, { before, update }] of perGuest) {
        watches.set(
          guestId,
          fields.map(({ field, isAdditional }) => ({
            field,
            isAdditional,
            // Baseline is the value AFTER our own write, not before it — the
            // pre-write value would make the first poll "succeed" on our own
            // change and stop watching before the automation ever ran.
            fromRaw: readGuestField(
              { ...(before ?? {}), ...update } as Guest,
              field,
              isAdditional,
            ),
          })),
        );
      }
      armGuestReconcile({ eventId, watches });
    },
    [eventId, mayRun, watchedFieldsFor],
  );

  // Workflow actions (e.g. UPDATE_GUEST on guest created/updated) mutate guests
  // server-side in the queue consumer with NO client event — so the only way to
  // surface them without a manual reload is to re-pull on window focus and on a
  // light interval. refreshGuests is the silent reconcile (no table flash), so a
  // poll won't blank the grid. Skip while inline edits are pending to avoid
  // clobbering unsaved changes.
  const hasPendingRef = useRef(hasPendingChanges);
  hasPendingRef.current = hasPendingChanges;
  // A reconcile that arrived while an edit was staged, still owed.
  //
  // `guestReconciled` is ONE-SHOT: `reconcileScheduler` deletes the guest from
  // the wave before it broadcasts, so an event a listener drops is gone — no
  // second attempt, no queue. Sharing the poll's `hasPendingChanges` guard
  // therefore did not defer the automation's result, it discarded it, and one
  // untouched draft anywhere in the table froze every automation result until
  // the operator saved or discarded. A failed save re-arms that same flag,
  // which is what made it stick rather than pass.
  const reconcileOwedRef = useRef(false);
  useEffect(() => {
    const refreshIfIdle = () => {
      if (hasPendingRef.current) return;
      refreshGuests();
      invalidateContingent();
    };
    // The poll and the focus refresh may still be skipped — they repeat, and
    // repainting a cell somebody is typing in is the bug that guard exists for.
    // A reconcile is remembered instead and drained the moment the table is
    // idle again (see `drainOwedReconcile`).
    const onReconciled = () => {
      if (hasPendingRef.current) {
        reconcileOwedRef.current = true;
        return;
      }
      refreshGuests();
      invalidateContingent();
    };
    window.addEventListener("focus", refreshIfIdle);
    // The targeted reconcile lands within seconds of a workflow write; this
    // interval stays as the coarse backstop for the paths that cannot arm one
    // (a role without `workflows:read`, an unloaded workflow list). Both routes
    // funnel through `refreshGuests`, which re-applies the active filters and
    // sort — a workflow can move a row OUT of the current view, and patching
    // the row in place would never show that.
    window.addEventListener("guestReconciled", onReconciled);
    window.addEventListener("guestReconcileCoarse", onReconciled);
    // Skip the interval while the tab is hidden — the focus listener above
    // already pulls a fresh page the moment the user returns, so there's no
    // point polling a backgrounded tab.
    const poll = window.setInterval(() => {
      if (document.hidden) return;
      refreshIfIdle();
    }, 45000);
    return () => {
      window.removeEventListener("focus", refreshIfIdle);
      window.removeEventListener("guestReconciled", onReconciled);
      window.removeEventListener("guestReconcileCoarse", onReconciled);
      window.clearInterval(poll);
    };
  }, [refreshGuests, invalidateContingent]);

  // Drain the owed reconcile as soon as the table stops holding a draft —
  // whether the operator saved or discarded. Runs on the transition to idle,
  // so an ordinary poll tick does not double-fetch.
  useEffect(() => {
    if (hasPendingChanges) return;
    if (!reconcileOwedRef.current) return;
    reconcileOwedRef.current = false;
    refreshGuests();
    invalidateContingent();
  }, [hasPendingChanges, refreshGuests, invalidateContingent]);

  // Fetch the current page from the API. Reassigned every render so it always
  // sees the latest filters/sort/page/search. The navigation effect calls it
  // inside startTransition (dims); refreshGuests() calls it bare (silent).
  runFetchRef.current = async () => {
    const token = ++fetchTokenRef.current;
    // A response is stale the moment a newer request has been issued. Stale
    // responses are dropped entirely — rows, count, and the broken-filter flag.
    const isStale = () => token !== fetchTokenRef.current;
    try {
      // Create filters array including the archived filter
      const finalFilters = [
        ...(baseFilters ?? []),
        ...activeFilterState.filter,
        {
          key: "archived",
          operator: "=",
          value: archived ? "true" : "false",
        },
      ];

      if (isDuplicateMode) {
        const duplicateData = await findDuplicatesByField(
          eventId,
          duplicateFields,
          page - 1,
          pageSize
        );

        if (!duplicateData) throw new Error("unknown");
        if (
          "isServerError" in duplicateData &&
          duplicateData.isServerError === true
        )
          throwServerError(duplicateData);

        if (isStale()) return;

        setGuests(
          "duplicates" in duplicateData &&
            Array.isArray(duplicateData.duplicates)
            ? (duplicateData.duplicates as Guest[])
            : []
        );
        setTotalCount(
          "totalCount" in duplicateData ? duplicateData.totalCount : 0
        );
      } else {
        // Nested-conditions mode: the active filter carries a ConditionGroup
        // (word operators, real OR support). System-injected flat filters
        // (baseFilters) AND-merge into the tree; `archived` travels as a
        // request-level boolean, NOT a filter row. Legacy flat mode otherwise.
        const userConditions = activeFilterState.conditions;
        const conditionsMode =
          !!userConditions && hasNonEmptyConditions(userConditions);
        const guestData = await getGuests(
          eventId,
          search,
          page - 1,
          pageSize,
          // allFieldsUnfiltered (not allFields): a CUSTOM-role field allowlist
          // hides fields for column display, but a saved/shared filter or sort on
          // such a field must STILL be sent — keying on the allowlist-filtered set
          // would treat it as deleted and silently DROP it, broadening results to
          // rows the filter was meant to exclude. Column values stay masked by
          // field-scope; only row filtering/sorting is preserved here.
          // PB-TJ-3: "keep" is only legal because this is the UNFILTERED set —
          // an unresolved key here really is a deleted field, not one an
          // allowlist hid from this role. The backend refuses it by name and
          // the catch below fails the list closed instead of widening it.
          conditionsMode
            ? []
            : parseFiltersApiRequest(finalFilters, allFieldsUnfiltered, {
                onUnresolvedKey: "keep",
              }),
          parseSortByApiRequest([sortBy], allFieldsUnfiltered),
          sortOrder,
          undefined,
          undefined,
          // `groupBy` rides in the same opts bag as `conditions` and is
          // omitted entirely when empty, so an ungrouped request is byte-for-
          // byte the one that shipped before grouping existed.
          conditionsMode
            ? {
                conditions: mergeFlatIntoGroup(
                  userConditions,
                  baseFilters ?? [],
                ),
                archived,
                ...(groupBy ? { groupBy } : {}),
              }
            : groupBy
              ? { groupBy }
              : undefined,
        );

        if ("isServerError" in guestData && guestData.isServerError === true)
          throwServerError(guestData);

        if (isStale()) return;

        setGuests(guestData as Guest[]);
        setBrokenFilterKey(false);
        // Clear the complaint only when a GROUPED request succeeded.
        //
        // Clearing it on any success would clear it on the very next fetch —
        // the ungrouped one this rejection just triggered — so the reason would
        // be gone before anything could read it. "The grouping you asked for
        // was refused" stays true until you ask for a different one.
        if (groupBy) setGroupByRejected(null);
      }
    } catch (error) {
      // Duplicate mode: a refused or failed duplicates request must not sit
      // silently under a "duplicate mode" banner over the ordinary rows (that
      // was the old behaviour — console.error, rows kept, banner lying). Leave
      // the mode, and hand the code to the page so it can say why. The rows are
      // NOT blanked: on an entry failure they are still the list the user was
      // looking at, and leaving the mode refetches them anyway. Stale-guarded
      // like every other branch — a 403 that lands after the user already
      // cancelled the mode must neither toast nor re-leave a mode that is off.
      if (isDuplicateMode) {
        if (isStale()) return;
        const code =
          error instanceof Error && isBackendErrorCode(error.message) ? error.message : "unknown";
        const detail =
          error instanceof Error && typeof (error as { detail?: unknown }).detail === "string"
            ? ((error as { detail?: string }).detail as string)
            : undefined;
        setDuplicateRejected((previous) => ({ code, detail, seq: (previous?.seq ?? 0) + 1 }));
        // Only a REFUSAL ends the mode. A refusal is permanent — the same keys
        // will be refused again — so staying would leave a banner over rows it
        // does not describe. A transient failure (5xx, offline, an aborted
        // request) is a different animal: this fetch also runs on the 45-second
        // poll and on window focus, so dropping the mode on a blip would throw
        // the user back to the full list, on page 1, mid-task. Say it and keep
        // the mode; the next tick refetches.
        if (isDuplicateRefusal(code)) setDuplicateMode(false);
        console.error("Duplicate check failed:", error);
        return;
      }
      // PB-TJ-3: `unknownFilterKey` is the exception to the rule below. It is
      // not transient — the active filter references a field the event no
      // longer has, and it will 400 identically on every retry. Keeping the
      // rows already on screen would keep showing a list the filter can no
      // longer restrict, which is exactly the silent widening this closes. Fail
      // CLOSED (empty) and flag it so the list can say why.
      if (
        error instanceof Error
        && error.message.startsWith("unknownFilterKey")
      ) {
        if (isStale()) return;
        setBrokenFilterKey(true);
        setGuests([]);
        setTotalCount(0);
        return;
      }
      // A rejected GROUPING is the second non-transient failure, and it used to
      // be the worst one in this file. The backend refuses a groupBy it does
      // not allow (`validationError: groupBy …`) or a party set over the cap
      // (`groupTooLarge`); both 400 identically on every retry. Falling through
      // to the "keep the rows" branch below meant: nothing visibly happened,
      // the choice was still persisted, and the NEXT page load restored it into
      // a full-page error whose only control was "Reload" — with the picker
      // that would have cleared it gone with the page. Unrecoverable without
      // clearing site data.
      //
      // So: drop the grouping, say why, and let the list come back ungrouped.
      // `setGroupBy("")` also clears the stored preference (the persist effect
      // in MetaTableProvider writes every change), which is what stops the
      // brick from surviving a reload.
      if (error instanceof Error && isGroupByRejection(error.message)) {
        if (isStale()) return;
        // Through `nextGroupByRejection`, so a REPEAT of the same reason is a
        // new value. Writing the bare reason twice was writing the same string:
        // React bailed out of the update, the announcing effect's deps never
        // moved, and the second refusal passed in silence — on exactly the
        // retry the first message invited.
        setGroupByRejected((previous) =>
          nextGroupByRejection(
            previous,
            error.message.startsWith("groupTooLarge")
              ? "groupTooLarge"
              : "groupByNotAllowed",
          ),
        );
        // The two refusals get DIFFERENT treatment, and the page lane already
        // knew that — this one did not, so the provider deleted the preference
        // the page deliberately preserves. One rule, both lanes.
        if (error.message.startsWith("groupTooLarge")) {
          suspendGroupByForSession(eventId);
          suspendGroupBy();
        } else {
          setGroupBy("");
        }
        return;
      }
      // PB-FDC-1: do NOT blank the list on a failed fetch. A conditions-mode
      // refetch that 400s (e.g. a legacy bare-key filter the backend now
      // normalizes, or any transient server error) previously fell through to
      // `setGuests([])`, wiping the correctly-narrowed rows already on screen
      // and leaving the "No guests found" empty state under an ACTIVE filter
      // chip with zero error surface — indistinguishable from real data loss.
      // Empty-safe: keep the current rows and log, rather than silently
      // clearing them on an error.
      console.error("Failed to fetch guests:", error);
    }
  };

  // Navigation refetch: a filter/sort/page/search/archived/duplicate-mode
  // change replaces the whole dataset, so dimming the table behind the spinner
  // (via startTransition → isPending) is correct here. Post-mutation reconciles
  // go through refreshGuests() instead and stay silent.
  //
  // Deduped by REQUEST SIGNATURE, not by dep identity: the signature mirrors
  // exactly what runFetch would send (parsed filters/conditions, sort, page,
  // search, …), so the fetch fires only when the actual request would differ
  // from the one that produced the data on screen. This is load-bearing twice:
  //  - the ref is seeded with the mount signature because the host already
  //    fetched that exact request and passed it as `initialGuests` — a mount
  //    fetch would be a duplicate (and StrictMode's dev effect replay used to
  //    slip past the old boolean isFirstRender guard here);
  //  - `allFields` churns identity whenever FieldsProvider's derived layers
  //    resolve (activity-option pickers, built-in overlay query) with
  //    content-equal fields — keying the effect on it refetched + dimmed the
  //    table right after first paint.
  const userConditionsSig = activeFilterState.conditions;
  const conditionsModeSig =
    !!userConditionsSig && hasNonEmptyConditions(userConditionsSig);
  const requestSignature = JSON.stringify(
    isDuplicateMode
      ? { mode: "dup", eventId, duplicateFields, page, pageSize }
      : {
          mode: "list",
          eventId,
          search,
          page,
          pageSize,
          sortOrder,
          archived,
          sort: parseSortByApiRequest([sortBy], allFieldsUnfiltered),
          filters: conditionsModeSig
            ? null
            : parseFiltersApiRequest(
                [
                  ...(baseFilters ?? []),
                  ...activeFilterState.filter,
                  { key: "archived", operator: "=", value: archived ? "true" : "false" },
                ],
                allFieldsUnfiltered,
                { onUnresolvedKey: "keep" },
              ),
          conditions: conditionsModeSig
            ? mergeFlatIntoGroup(userConditionsSig, baseFilters ?? [])
            : null,
          // Mirrors the request byte-for-byte (useSignatureEffect's rule):
          // grouping changes what the response IS, so a change here must refetch.
          groupBy: groupBy || null,
        },
  );
  useSignatureEffect(requestSignature, () => {
    startTransition(async () => {
      await runFetchRef.current();
    });
  });

  // Shared helper for removing items and handling pagination
  const removeGuestsAndRefresh = useCallback(
    (guestIds: string[]) => {
      // Party grouping breaks BOTH halves of the optimistic path below, so it
      // takes the honest one instead: re-read the rows, re-ask for the counts.
      //
      // 1. `totalCount` counts PARTIES there, and `guestIds` are PEOPLE.
      //    Deleting two companions removes zero parties and used to drop the
      //    pagination denominator by two.
      // 2. `guestsRef.current` holds only party HEADS — a companion's id
      //    matches nothing, `remaining` stays the full page, the
      //    `< pageSize` refetch never fires, and the deleted companions stay
      //    rendered underneath their head.
      // 3. `selectionTotalCount` resolves to a separately fetched people count
      //    that no mutation writes at all, so every bulk affordance kept
      //    quoting the pre-delete number until the user touched a filter.
      if (isPartyGroup(groupBy)) {
        refreshGuests();
        refreshCounts();
        return;
      }

      const removedCount = guestIds.length;
      setTotalCount((prev) => prev - removedCount);

      const remaining = guestsRef.current.filter(
        (guest) => !guestIds.includes(guest.guestId!)
      );
      setGuests(remaining);

      if (remaining.length === 0) {
        setPage((p) => (p > 1 ? p - 1 : p));
      } else if (remaining.length < pageSize) {
        refreshGuests();
      }
    },
    [pageSize, setPage, setTotalCount, refreshGuests, refreshCounts, groupBy],
  );

  // NOTE on selection lifecycle: these bulk mutators intentionally do NOT call
  // `clearBulkSelection()` from `MetaTableProvider`. The caller (dropdown, details
  // panel, etc.) owns the selection UI and should clear it on success; wiring it in
  // here would paper over bugs where the banner state and the list state drift apart.
  const archiveGuests = useCallback(
    async (selection: BulkSelection) => {
      if (selection.mode === "ids" && selection.ids.length === 0) return;

      const response = await archiveGuestsApi(eventId, selection);
      if (response && response.isServerError === true) {
        // A partial refusal still moved the other rows — re-read before the
        // caller shows the per-row reasons (M-97).
        refreshGuests();
        throwServerError(response);
      }

      if (selection.mode === "ids") {
        removeGuestsAndRefresh(selection.ids);
      } else {
        refreshGuests();
      }
      invalidateContingent();
    },
    [eventId, removeGuestsAndRefresh, refreshGuests, invalidateContingent]
  );

  const unarchiveGuests = useCallback(
    async (selection: BulkSelection) => {
      if (selection.mode === "ids" && selection.ids.length === 0) return;

      const response = await unarchiveGuestsApi(eventId, selection);
      if (response && response.isServerError === true) {
        // A partial refusal still moved the other rows — re-read before the
        // caller shows the per-row reasons (M-97).
        refreshGuests();
        throwServerError(response);
      }

      if (selection.mode === "ids") {
        removeGuestsAndRefresh(selection.ids);
      } else {
        refreshGuests();
      }
      invalidateContingent();
    },
    [eventId, removeGuestsAndRefresh, refreshGuests, invalidateContingent]
  );

  const bulkEditGuests = useCallback(
    async (selection: BulkSelection, updates: BulkEditableUpdates) => {
      if (selection.mode === "ids" && selection.ids.length === 0) return;

      const response = await bulkEditGuestsApi(eventId, selection, updates);
      if (response && response.isServerError === true)
        throwServerError(response);

      refreshGuests();
      invalidateContingent();
    },
    [eventId, refreshGuests, invalidateContingent]
  );

  const deleteGuests = useCallback(
    async (selection: BulkSelection, options?: { permanent?: boolean }) => {
      if (selection.mode === "ids" && selection.ids.length === 0) return null;

      const response = options
        ? await deleteGuestsApi(eventId, selection, options)
        : await deleteGuestsApi(eventId, selection);
      if (isServerErr(response)) throwServerError(response);

      if (selection.mode === "ids") {
        removeGuestsAndRefresh(selection.ids);
      } else {
        refreshGuests();
      }
      invalidateContingent();
      return response;
    },
    [eventId, removeGuestsAndRefresh, refreshGuests, invalidateContingent]
  );

  const duplicateGuest = useCallback(
    async (_guestId: string) => {
      // After duplication, refresh to get the new guest
      refreshGuests();
    },
    [refreshGuests]
  );

  // Core create. `decision` is the admin's capacity-popup choice (null on the
  // first attempt). On the first attempt a full event comes back as
  // `capacityDecisionRequired`: open the 3-option popup and throw the
  // CAPACITY_DECISION_PENDING sentinel so CreateGuestBody closes silently.
  //
  // Takes a LIST, because `POST /guests/{eventId}/batch` has always been a
  // batch door and the placeholder-companion quick-add sends N rows through it
  // in one request.
  //
  // Every GATE on that door is all-or-nothing — the field allowlist, the row
  // filter, the additionalFields validator and the capacity reservation each
  // reject the whole list before anything is built or written — so there is
  // exactly one decision to make and one error to show for the whole call.
  // The INSERT itself is not transactional (`insertMany(ordered:false)`, no
  // session), so a mid-insert database failure can leave some rows committed
  // behind an error response. Rare, and not something this layer can repair;
  // `refreshGuests()` is what makes the truth visible either way.
  const performCreate = useCallback(
    async (guests: Guest[], decision: "waitlist" | "normal" | null): Promise<Guest[]> => {
      if (guests.length === 0) return [];
      const payload: Guest[] = guests.map((guest) =>
        decision === "waitlist"
          ? { ...guest, forceWaitlist: true }
          : decision === "normal"
            ? { ...guest, overrideRules: true }
            : guest,
      );

      let newGuestIds = await createGuests(eventId, payload);

      if ("isServerError" in newGuestIds && newGuestIds.isServerError === true) {
        if (decision === null && isCapacityDecision(newGuestIds)) {
          setDecisionError(null);
          setCapacityDecision({
            guests,
            context: capacityContextOf(newGuestIds),
            kind: "create",
          });
          throw new Error(CAPACITY_DECISION_PENDING);
        }
        // throwServerError carries the per-field additional-field violations
        // (create-batch emits the same shape as update) so CreateGuestBody
        // can render them inline — plus the raw backend `detail`.
        throwServerError(newGuestIds);
      }

      newGuestIds = newGuestIds as string[];
      if (!newGuestIds || newGuestIds.length === 0) {
        throw new Error("Failed to create guest");
      }

      // Reflect the stored outcome optimistically; refresh corrects any drift.
      //
      // Every branch is SYSTEM because the create door
      // (`POST /guests/{eventId}/batch`) writes SYSTEM on every row — main guest
      // or companion, capacity override or not, whatever the event's
      // registration mode. A hand-added guest is not a request anybody made, so
      // it belongs in neither the open-requests list nor the history (both of
      // which select on acceptanceState alone).
      //
      // Billing: unchanged on a DIRECT-mode event, where the door already wrote
      // ACCEPTED and contingent counts ACCEPTED and SYSTEM identically. On a
      // REQUEST-mode event it DOES change — that door used to write PENDING,
      // which costs nothing until the request is approved, and SYSTEM is charged
      // at create. That was the accepted trade for not filing hand-added guests
      // as requests; do not repeat the older, shorter claim that billing is
      // untouched everywhere.
      //
      // The default branch used to send NO acceptanceState at all, which left
      // the status cell blank until refreshGuests() landed, and the "normal"
      // branch claimed ACCEPTED — a value the server has never stored since this
      // door was fixed. Both were corrected together: this file must not invent
      // a state the backend does not write.
      //
      // Ids come back positionally, and a row whose id we cannot name is a row
      // the optimistic list must not carry: `refreshGuests()` below is what
      // makes the difference invisible, so a short response simply yields fewer
      // optimistic rows rather than rows with an undefined guestId.
      // Only a ONE-row create may claim WAITLISTED. The backend applies the
      // waitlist overlay per capacity-hit row (`GuestResource`: `if (hit &&
      // forceWaitlist)`), so with two seats free and four rows it admits two
      // and waitlists two — stamping all four would show two guests as
      // waitlisted who are not, and `refreshGuests()` only repaints that when
      // no inline edit is pending. With a single row the hit is what produced
      // the popup in the first place, so the old claim still holds.
      const claimWaitlisted = decision === "waitlist" && guests.length === 1;
      const created: Guest[] = guests.slice(0, newGuestIds.length).map((guest, index) =>
        claimWaitlisted
          ? {
              ...guest,
              guestId: (newGuestIds as string[])[index],
              state: GuestState.WAITLISTED,
              acceptanceState: AcceptanceState.SYSTEM,
            }
          : {
              ...guest,
              guestId: (newGuestIds as string[])[index],
              acceptanceState: AcceptanceState.SYSTEM,
            },
      );

      setGuests((prev) => [...created, ...prev]);
      if (isPartyGroup(groupBy)) {
        // Same unit mismatch as the delete lane: a created person is not
        // necessarily a new party (a companion joins an existing one), and the
        // people count behind `selectionTotalCount` is not written here at all.
        refreshCounts();
      } else {
        setTotalCount((prev) => prev + created.length);
      }
      // Everyone else who holds these rows — today the companion sidebar —
      // learns about them here, on EVERY create lane including the two capacity
      // confirmations below, instead of only where a caller remembered to say so.
      window.dispatchEvent(
        new CustomEvent<GuestsCreatedDetail>("guestsCreated", {
          detail: { eventId, guests: created },
        }),
      );
      refreshGuests();
      invalidateContingent();

      return created;
    },
    [eventId, refreshGuests, setTotalCount, refreshCounts, groupBy, invalidateContingent]
  );

  const addGuest = useCallback(
    async (guest: Guest) => {
      const created = await performCreate([guest], null);
      // The single-guest door keeps its old contract (it returns THE guest, and
      // callers read `.guestId` off it straight away), so an empty result is an
      // error here even though the batch door tolerates one.
      if (created.length === 0) throw new Error("Failed to create guest");
      return created[0];
    },
    [performCreate]
  );

  const addGuests = useCallback(
    (guests: Guest[]) => performCreate(guests, null),
    [performCreate]
  );

  const buildUpdateGuests = useCallback(
    (guestId: string, field: Field, newValue: string | string[]) => {
      if (!guestId || !field) return;

      setGuests((prevGuests) => {
        // An existence guard, nothing more (`parseGuestsSpecial` never reads
        // the row) — but it has to see the whole party tree. A flat `find`
        // missed every companion on the party-grouped list, and the edit was
        // dropped without a word: nothing staged, no pending flag, no save.
        const existingGuest = findGuestById(prevGuests, guestId);
        if (!existingGuest) return prevGuests;

        const current = pendingUpdatesRef.current;
        let update = current.find((u) => u.guestId === guestId);
        if (!update) {
          update = { guestId: guestId } as Guest;
        } else {
          update = { ...update };
        }
        parseGuestsSpecial(existingGuest, update, field, newValue);
        const filtered = current.filter((item) => item.guestId !== guestId);
        pendingUpdatesRef.current = [...filtered, update];
        // The admin just typed into this row, so whatever is in the draft for it
        // is theirs now — the override cleanup must not discard it.
        restoredDraftIdsRef.current.delete(guestId);
        setHasPendingChanges(true);

        return prevGuests;
      });
    },
    []
  );

  const [seatRelease, setSeatRelease] = useState<SeatReleaseWarning[] | null>(null);

  /**
   * Which of these pending updates would silently free a seat?
   *
   * `GuestService.releasesSeats` unassigns a guest from EVERY seat they hold
   * the moment they are declined (either column) or archived. That cascade is
   * deliberate and stays — but it used to happen with nothing on screen, so an
   * organiser could change one dropdown and lose a seating arrangement they had
   * spent an afternoon on, learning about it only by opening the plan.
   *
   * Only `state` and `archived` are checked here, because those are the two an
   * organiser writes from a guest-LIST surface. `acceptanceState` also releases
   * seats, but it is written from the Requests view, where the guest is not a
   * guest-list member and has no seat to lose.
   *
   * `seatCount` / `seatRoomIds` are denormalized rollups that already ride
   * GUEST_FIELDS, so this costs no request. A guest whose rollup is stale-low
   * simply does not get a warning — the release itself is unaffected, and
   * warning too little is the safe direction for a confirmation that must not
   * cry wolf.
   */
  const seatReleasingUpdates = useCallback(
    (pending: Guest[]): SeatReleaseWarning[] => {
      const out: SeatReleaseWarning[] = [];
      for (const update of pending) {
        // A companion can hold a seat too; on the party-grouped list it is
        // nested under its head, where a flat `find` never looked.
        const guest = update.guestId
          ? findGuestById(guestsRef.current, update.guestId)
          : undefined;
        if (!guest) continue;
        const seatCount = guest.seatCount ?? 0;
        if (seatCount <= 0) continue;
        const declines =
          update.state === GuestState.DECLINED && guest.state !== GuestState.DECLINED;
        const archives = update.archived === true && guest.archived !== true;
        if (!declines && !archives) continue;
        out.push({
          // `guestId` is optional on the Guest shape but present on every
          // pending update by construction (parseGuestsSpecial seeds it), and a
          // row we cannot name is a row we cannot warn about — skip rather than
          // render an empty bullet.
          guestId: update.guestId ?? guest.guestId ?? "",
          name: [guest.firstName, guest.lastName].filter(Boolean).join(" ").trim(),
          seatCount,
          reason: declines ? "declined" : "archived",
        });
      }
      return out;
    },
    [],
  );

  const runSaveUpdateGuests = useCallback(async () => {
    const pending = pendingUpdatesRef.current;
    if (pending.length === 0) return;

    // PB-TI-2: a retry must not show the previous attempt's error.
    setSaveError(null);
    setIsSavingGuests(true);
    pendingUpdatesRef.current = [];

    // Defense-in-depth: never send an additionalFields key (or its clearFields
    // $unset path) for a custom field that no longer exists on the event — or
    // for one that has since become COMPUTED.
    //
    // ⚠ This used to say "parseGuestsSpecial copies the guest's whole
    // additionalFields map". It does not, and has not for a while: it seeds
    // from the UPDATE's own accumulated map, so this lane carries a DELTA and
    // an untouched key is simply absent. The stale sentence mattered — it made
    // a vacuous test look like a real pin (edit an ordinary column, assert the
    // derived one is absent; it always is).
    //
    // What the prune really defends here is a value that was STAGED while the
    // column was still writable and is saved after it stopped being: a field
    // deleted, or turned into a computed one, between the keystroke and the
    // save. Nothing about the pending queue re-checks the catalogue it was
    // filled against. Using
    // allFieldsUnfiltered (not allFields) so a CUSTOM-role hidden field isn't
    // mistaken for deleted. Builds fresh objects — parseGuestsSpecial aliases
    // the live guest's additionalFields, so we must not mutate in place.
    const validCustomFieldNames = writableCustomFieldNames;
    const updatesToSave = pending.map((update) =>
      pruneStaleAdditionalFields(update, validCustomFieldNames)
    );

    // Snapshot current guests for rollback on failure
    const prevGuests = guestsRef.current;

    try {
      const updatesMap = new Map(
        updatesToSave.map((update) => [update.guestId, update])
      );

      // `mergeGuestPatch`, not a spread. `update.additionalFields` is a
      // session DELTA by construction (parseGuestsSpecial seeds it from the
      // update's own accumulated map, because the backend dot-paths each key),
      // so spreading it over the row dropped every custom-field value the edit
      // did not mention — a second custom column went blank on save and stayed
      // blank until a reload, since this path never refetches. It also applies
      // the `additionalFields.<name>` unsets the same map encodes as
      // `clearFields`, and keeps that bookkeeping off the row.
      // Through the party tree, so a companion's cell shows its saved value
      // the same instant a head's does. NOT a refetch: this path never
      // refetches after a save (see above), and `refreshGuests` is not gated
      // on pending drafts — a refetch here would repaint every other row the
      // operator is still typing into.
      setGuests((prev) =>
        mapGuestTree(prev, (guest) => {
          const update = updatesMap.get(guest.guestId);
          return update ? mergeGuestPatch(guest, update) : guest;
        })
      );

      const response = await updateGuests(eventId, updatesToSave);
      if (response && "isServerError" in response && response.isServerError) {
        // Partial failure (batchUpdatePartialFailure): succeeded guests
        // persisted (keep their optimistic value); revert only the failed
        // ones, and surface those blocked by the capacity rule for the
        // "promote anyway?" confirmation.
        const failed = response.failures ?? [];
        if (failed.length > 0) {
          const failedSet = new Set(failed.map((f) => f.guestId));
          // The snapshot covers the tree for the same reason the merge walks
          // it: a failed COMPANION row has to snap back too, or it keeps
          // showing the value the banner just said was refused. TOP LEVEL
          // FIRST and never overwritten: on the flat list a companion is both
          // its own row and a `{guestId, firstName, lastName}` chip under its
          // head, and a "last entry wins" map let the chip replace the full
          // row whenever the head sorted after the companion — every other
          // cell went blank after a refused save.
          const prevById = new Map<string | undefined, Guest>();
          for (const g of prevGuests) prevById.set(g.guestId, g);
          for (const g of flattenGuestTree(prevGuests)) {
            if (!prevById.has(g.guestId)) prevById.set(g.guestId, g);
          }
          setGuests((prev) =>
            mapGuestTree(prev, (g) =>
              g.guestId && failedSet.has(g.guestId) ? (prevById.get(g.guestId) ?? g) : g
            )
          );
          // Restore the failed rows' drafts, exactly as the catch path below
          // does. Most per-guest codes ARE retryable (approvalBlockedByRules
          // with an override, versionConflict, contingentExceeded), so dropping
          // the draft would throw away typing under a banner that says "retry".
          // Note the pending ref was already emptied above, before the request
          // — anything in it now was re-added by a blur, so don't clobber it.
          const alreadyStaged = new Set(
            pendingUpdatesRef.current.map((p) => p.guestId)
          );
          const restored = updatesToSave.filter(
            (u) =>
              u.guestId &&
              failedSet.has(u.guestId) &&
              !alreadyStaged.has(u.guestId)
          );
          for (const u of restored) {
            if (u.guestId) restoredDraftIdsRef.current.add(u.guestId);
          }
          pendingUpdatesRef.current = [...pendingUpdatesRef.current, ...restored];
          setHasPendingChanges(pendingUpdatesRef.current.length > 0);

          const blockedSet = new Set(approvalBlockedGuestIds(response));
          const blockedUpdates = updatesToSave.filter(
            (u) => u.guestId && blockedSet.has(u.guestId)
          );
          if (blockedUpdates.length > 0) {
            setDecisionError(null);
            setCapacityDecision({
              guests: blockedUpdates,
              context: capacityContextOf(response),
              kind: "promote",
            });
          }
          // The revert above is indistinguishable from "nothing happened"
          // unless we say so — this branch used to return silently, so an
          // inline edit the server rejected just snapped back with no message.
          // Structured, not a string: the reason lives in failures[], and the
          // envelope code alone ("batchUpdatePartialFailure") names nothing.
          setSaveError({
            message: response.message,
            detail: response.detail,
            failures: failed,
          });
          return;
        }
        throwServerError(response);
      }

      // After save + render flush, blur may have re-added the same values.
      // Discard stale pending entries that match what was just saved.
      const savedIds = new Set(updatesToSave.map((u) => u.guestId));
      pendingUpdatesRef.current = pendingUpdatesRef.current.filter(
        (p) => !savedIds.has(p.guestId)
      );
      setHasPendingChanges(pendingUpdatesRef.current.length > 0);
      invalidateContingent();
      // An inline Status edit fires GUEST_UPDATED plus the derived lifecycle
      // triggers, and any UPDATE_GUEST pipe on those runs after this response.
      // Watch the rows we just wrote so the grid shows the automation's result
      // instead of ours.
      armReconcileForUpdates(updatesToSave, prevGuests);
      // Party MEMBERSHIP is the one thing the optimistic merge cannot follow:
      // an inline `guestType` / `mainGuest` edit moves a person between
      // parties (or out of one), and on the party-grouped list that is a
      // change of SHAPE, not of a value — the same reason delete and create
      // refetch here. Only on the fully successful path: a partial failure
      // returned above with drafts restored, and this refetch is not gated on
      // them.
      if (
        isPartyGroup(groupBy) &&
        updatesToSave.some((u) => u.guestType !== undefined || u.mainGuest !== undefined)
      ) {
        refreshGuests();
      }
    } catch (error) {
      console.error("Failed to update guests:", error);
      // Revert optimistic update
      setGuests(prevGuests);
      pendingUpdatesRef.current = updatesToSave;
      setHasPendingChanges(true);
      // PB-TI-2: the revert above is indistinguishable from "nothing happened"
      // unless we say so. The draft is deliberately KEPT (pendingUpdatesRef is
      // restored and hasPendingChanges re-armed) so the operator can retry
      // rather than retype — the error surface is what tells them to.
      const rich = error as Partial<SaveErrorPayload> | undefined;
      setSaveError({
        message:
          error instanceof Error && error.message ? error.message : "unknown",
        detail: typeof rich?.detail === "string" ? rich.detail : undefined,
        // A throw can carry failures[] too (throwServerError copies them), e.g.
        // when the batch had NO usable per-guest rows and fell through above.
        failures: Array.isArray(rich?.failures) ? rich.failures : undefined,
      });
    } finally {
      setIsSavingGuests(false);
    }
    // `groupBy` and `refreshGuests` ride for the party-membership refetch
    // above; both are stable across an edit session (the picker is disabled).
  }, [eventId, allFieldsUnfiltered, invalidateContingent, groupBy, refreshGuests]);

  /**
   * The guest-list save door. Everything that edits a guest row inline funnels
   * through here, which is why the seat warning sits at this one place rather
   * than on each cell: the status dropdown, a bulk paste and a multi-row edit
   * all reach it, and a per-cell dialog would fight the pending-save batching.
   *
   * The pending updates are deliberately NOT cleared on the warning path — a
   * cancel has to leave the edit sitting in the grid so the organiser can put
   * the status back. Only {@link runSaveUpdateGuests} clears them, and it only
   * runs once there is nothing left to warn about or the warning was accepted.
   */
  const saveUpdateGuests = useCallback(async () => {
    const pending = pendingUpdatesRef.current;
    if (pending.length === 0) return;
    const warnings = seatReleasingUpdates(pending);
    if (warnings.length > 0) {
      setSeatRelease(warnings);
      return;
    }
    await runSaveUpdateGuests();
  }, [seatReleasingUpdates, runSaveUpdateGuests]);

  const confirmSeatRelease = useCallback(async () => {
    setSeatRelease(null);
    await runSaveUpdateGuests();
  }, [runSaveUpdateGuests]);

  const cancelSeatRelease = useCallback(() => setSeatRelease(null), []);

  const dismissSaveError = useCallback(() => setSaveError(null), []);

  const discardPendingUpdates = useCallback(() => {
    pendingUpdatesRef.current = [];
    setHasPendingChanges(false);
  }, []);

  // "Trotzdem als Gast hinzufügen" — admit over the cap. For a create that
  // means a fresh over-cap create; for a promote it re-sends the blocked
  // promotions with overrideRules + acceptanceState=ACCEPTED (backend writes
  // the GUEST_APPROVAL_OVERRIDE audit row).
  const confirmCapacityNormal = useCallback(async () => {
    if (!capacityDecision) return;
    setDecisionLoading(true);
    try {
      if (capacityDecision.kind === "create") {
        await performCreate(capacityDecision.guests, "normal");
      } else {
        const payload = capacityDecision.guests.map((u) => ({
          ...u,
          overrideRules: true,
          acceptanceState: AcceptanceState.ACCEPTED,
        }));
        const byId = new Map(payload.map((u) => [u.guestId, u]));
        setGuests((prev) =>
          mapGuestTree(prev, (g) =>
            g.guestId && byId.has(g.guestId) ? { ...g, ...byId.get(g.guestId) } : g,
          )
        );
        const response = await updateGuests(eventId, payload);
        if (response && "isServerError" in response && response.isServerError) {
          throwServerError(response);
        }
        // The override SUCCEEDED for these rows — and the partial-failure
        // branch left two artefacts behind that are now lies: the red banner
        // ("could not be saved") over rows that just saved, and the restored
        // draft, which still holds the PRE-override payload. That draft keeps
        // Save armed, and pressing it re-sends the very write the server
        // already rejected, without `overrideRules`.
        //
        // Scoped to the overridden ids on purpose: the same batch can fail for
        // reasons this modal never offered to override (versionConflict,
        // unknownAdditionalFieldKey), and those rows must keep both their
        // banner line and their draft.
        //
        // ...and only the entries that ARE that restored payload. A cell the
        // admin typed while the banner was up produced a fresh draft entry for
        // the same guest, and dropping by guestId threw it away: Save went
        // quiet, the refresh repainted the old value, and nothing on screen
        // said anything had been lost.
        const overriddenIds = new Set(
          payload.map((u) => u.guestId).filter(Boolean) as string[]
        );
        pendingUpdatesRef.current = pendingUpdatesRef.current.filter(
          (p) =>
            !p.guestId ||
            !overriddenIds.has(p.guestId) ||
            !restoredDraftIdsRef.current.has(p.guestId)
        );
        for (const id of overriddenIds) restoredDraftIdsRef.current.delete(id);
        setHasPendingChanges(pendingUpdatesRef.current.length > 0);
        setSaveError((prev) => {
          if (!prev || typeof prev === "string") return null;
          const rest = (prev.failures ?? []).filter(
            (f) => !overriddenIds.has(f.guestId)
          );
          return rest.length > 0 ? { ...prev, failures: rest } : null;
        });
      }
      // Went through: only now does the popup close (R3, M-10).
      setCapacityDecision(null);
    } catch (error) {
      console.error("Capacity override failed:", error);
      // The popup stays open and says why.
      setDecisionError(error);
    } finally {
      setDecisionLoading(false);
      refreshGuests();
      invalidateContingent();
    }
  }, [capacityDecision, performCreate, eventId, refreshGuests, invalidateContingent]);

  // "Auf Warteliste setzen" — create only (a promote target is already
  // waitlisted, so that button is hidden for kind "promote").
  const confirmCapacityWaitlist = useCallback(async () => {
    if (!capacityDecision || capacityDecision.kind !== "create") return;
    setDecisionLoading(true);
    try {
      await performCreate(capacityDecision.guests, "waitlist");
      setCapacityDecision(null);
    } catch (error) {
      console.error("Waitlist create failed:", error);
      setDecisionError(error);
    } finally {
      setDecisionLoading(false);
      refreshGuests();
      invalidateContingent();
    }
  }, [capacityDecision, performCreate, refreshGuests, invalidateContingent]);

  const cancelCapacity = useCallback(() => setCapacityDecision(null), []);

  const updateGuest = useCallback(async (
    newGuest: Guest,
    avoidUpdate: boolean,
    prevMainGuestId: string,
    currentMainGuestId: string
  ) => {
    try {
      // A COMPUTED column is derived and refused on every write door. The
      // drawer seeds its form from the STORED guest, and `buildUpdateGuests`
      // used to send the whole `additionalFields` map as soon as any ONE key
      // differed, so the derived value rode along on every save and the
      // server answered 400 for the batch — i.e. no guest on an event with a
      // computed field could be edited at all. The builder now sends a per-key
      // delta, so a computed key only rides when its own value differs (a
      // stale copy against a recomputed value still can) — the prune stays.
      //
      // Dropped here rather than in `buildUpdateGuests`: this is the single
      // door every drawer save goes through, and the batch lane below already
      // drops unwritable keys for the very same reason (a since-deleted field
      // and its `unknownAdditionalFieldKey`).
      const response = await updateGuests(eventId, [
        pruneStaleAdditionalFields(newGuest, writableCustomFieldNames),
      ]);
      if (isServerErrorResponse(response)) {
        // throwServerError preserves the backend's per-field additional-field
        // violations on the thrown error so GuestActions can render each one
        // inline (bound-aware) instead of one generic banner — plus `detail`.
        throwServerError(response);
      }

      if (!avoidUpdate) {
        // `mergeGuestPatch`, not a raw spread: `newGuest` is the popup's DIFF.
        // Its `additionalFields` map has every persisted-IMAGE key stripped
        // (stripPersistedImageEchoes), so overwriting the row's map wholesale
        // blanked the table's IMAGE columns after any unrelated AF edit; it
        // also left the bookkeeping `clearFields` array sitting on the row and
        // ignored the unsets it asks for. Same semantics the popup and the
        // requests providers already apply to this exact patch.
        setGuests((prev) =>
          prev.map((g) =>
            g.guestId === newGuest.guestId ? mergeGuestPatch(g, newGuest) : g
          )
        );
      } else {
        setGuests((prev) => {
          if (prevMainGuestId === currentMainGuestId) {
            return prev.map((g) =>
              g.guestId === prevMainGuestId
                ? {
                    ...g,
                    companions: g.companions?.some(
                      (c) => c.guestId === newGuest.guestId
                    )
                      ? g.companions.map((c) =>
                          c.guestId === newGuest.guestId ? mergeGuestPatch(c, newGuest) : c
                        )
                      : [...(g.companions || []), newGuest],
                  }
                : g
            );
          }
          // Different main guest - remove from old, add to new. The moved
          // companion is carried over as a whole: `newGuest` is the save DIFF,
          // so appending it bare gave the new main a nameless companion chip
          // ("-" in the association cell) until the next list fetch.
          const moved = prev
            .find((g) => g.guestId === prevMainGuestId)
            ?.companions?.find((c) => c.guestId === newGuest.guestId);
          const arriving = moved ? mergeGuestPatch(moved, newGuest) : newGuest;
          return prev.map((g) => {
            if (g.guestId === prevMainGuestId) {
              return {
                ...g,
                companions:
                  g.companions?.filter(
                    (c) => c.guestId !== newGuest.guestId
                  ) || [],
              };
            }
            if (g.guestId === currentMainGuestId) {
              return {
                ...g,
                companions: g.companions?.some(
                  (c) => c.guestId === newGuest.guestId
                )
                  ? g.companions.map((c) =>
                      c.guestId === newGuest.guestId ? mergeGuestPatch(c, newGuest) : c
                    )
                  : [...(g.companions || []), arriving],
              };
            }
            return g;
          });
        });
      }
      // The requests page renders its rows from RegistrantsProvider/
      // CompanionsProvider state, and the popup there runs on an ISOLATED
      // GuestsProvider (StandaloneGuestDetail) — the setGuests above can't
      // reach them. Broadcast the confirmed patch; those providers merge it
      // into matching rows. The guest-detail popup listens as well, on every
      // page: it renders a guest object its opener captured, which the
      // setGuests above does not touch. That is a second object, not a second
      // application to the same one, and mergeGuestPatch is idempotent
      // regardless.
      if (newGuest.guestId) {
        window.dispatchEvent(
          new CustomEvent<GuestDataUpdatedDetail>("guestDataUpdated", {
            detail: { eventId, guestId: newGuest.guestId, patch: newGuest },
          }),
        );
        // The broadcast only reaches a MOUNTED requests provider; a tab switch
        // unmounts it and the remount re-seeds from the cached snapshot. Merge
        // the confirmed patch there too, or the edit reverts on the way back.
        patchGuestInRequestsCache(queryClient, eventId, newGuest.guestId, newGuest);
      }
      invalidateContingent();
    } catch (error) {
      console.error("Failed to update guest:", error);
      throw error;
    }
    // ⚠ `writableCustomFieldNames` is listed because this callback PRUNES with
    // it. Left out, the drawer keeps the field catalogue it was created with:
    // an organiser who adds a field mid-session and then edits a guest has the
    // new field's value stripped out of every drawer save until a remount —
    // silently, because a pruned key looks exactly like one nobody typed.
    // `runSaveUpdateGuests` lists its own catalogue dependency for this reason.
  }, [eventId, invalidateContingent, queryClient, writableCustomFieldNames]);

  const checkInGuest = useCallback(
    async (guestId: string, performedByType?: string): Promise<{ warning?: string } | void> => {
      const guest = guestsRef.current.find((g) => g.guestId === guestId);
      const currentCheckedIn = !!guest?.checkedIn;

      const result = await checkInGuestApi(eventId, guestId, !currentCheckedIn, performedByType);

      if (result && typeof result === "object" && "isServerError" in result) {
        throwServerError(result);
      }

      setGuests((prev) =>
        prev.map((g) =>
          g.guestId === guestId ? { ...g, checkedIn: !currentCheckedIn } : g
        )
      );
      invalidateContingent();

      return result as { warning?: string } | void;
    },
    [eventId, invalidateContingent]
  );

  const checkInGuests = useCallback(
    async (
      guestIds: string[],
      checkIn: boolean,
      performedByType?: string,
    ): Promise<PartyCheckInReport> => {
      const report = await runPartyCheckIn(
        eventId,
        guestIds,
        checkIn,
        performedByType,
        // Per person, the moment the server confirmed — so the rows behind the
        // dialog flip one by one instead of all at the end, and a run that
        // stops halfway still leaves the list telling the truth.
        (guestId) => {
          // Party-grouped rows: a companion is not a top-level entry, it
          // lives under its head. `mapGuestTree` patches it there too, or the
          // row the operator just ticked keeps showing the old state.
          setGuests((prev) =>
            mapGuestTree(prev, (g) =>
              g.guestId === guestId ? { ...g, checkedIn: checkIn } : g,
            ),
          );
          // The guest-detail popup and the requests page render their own guest
          // objects, which `setGuests` cannot reach. `updateGuest` broadcasts
          // for exactly that reason; the single-guest check-in never did, which
          // is why a popup left open beside the list went stale.
          window.dispatchEvent(
            new CustomEvent<GuestDataUpdatedDetail>("guestDataUpdated", {
              detail: { eventId, guestId, patch: { checkedIn: checkIn } },
            }),
          );
        },
      );

      // Once for the whole party, not per person: both listeners behind this
      // are refetches.
      invalidateContingent();
      if (report.results.some((r) => r.status !== "failed")) {
        window.dispatchEvent(new Event("checkInActionCompleted"));
      }
      return report;
    },
    [eventId, invalidateContingent],
  );

  const contextValue = useMemo(() => ({
    updateGuest,
    checkInGuest,
    checkInGuests,
    refreshGuests,
    eventId,
    saveUpdateGuests,
    buildUpdateGuests,
    duplicateGuest,
    archiveGuests,
    unarchiveGuests,
    bulkEditGuests,
    addGuest,
    addGuests,
    deleteGuests,
    discardPendingUpdates,
    hasPendingChanges,
    saveError,
    isSavingGuests,
    dismissSaveError,
    guests,
    brokenFilterKey,
    groupByRejected,
    duplicateRejected,
    loading: isPending,
  }), [updateGuest, checkInGuest, checkInGuests, refreshGuests, eventId, saveUpdateGuests, buildUpdateGuests, duplicateGuest, archiveGuests, unarchiveGuests, bulkEditGuests, addGuest, addGuests, deleteGuests, discardPendingUpdates, hasPendingChanges, saveError, isSavingGuests, dismissSaveError, guests, brokenFilterKey, groupByRejected, duplicateRejected, isPending]);

  return (
    <GuestsContext.Provider value={contextValue}>
      {children}
      {seatRelease && seatRelease.length > 0 && (
        <SeatReleaseConfirm
          warnings={seatRelease}
          isSaving={isSavingGuests}
          onConfirm={() => void confirmSeatRelease()}
          onCancel={cancelSeatRelease}
        />
      )}
      {capacityDecision && (
        <CapacityDecisionModal
          context={capacityDecision.context}
          kind={capacityDecision.kind}
          isLoading={decisionLoading}
          onWaitlist={confirmCapacityWaitlist}
          onNormal={confirmCapacityNormal}
          onCancel={cancelCapacity}
          error={decisionError ?? undefined}
        />
      )}
    </GuestsContext.Provider>
  );
};

/**
 * The guests context WITHOUT the throw — for a component that can legitimately
 * render outside a guest surface and must degrade instead of taking the page
 * down. The throwing {@link useGuests} stays the default: a consumer that needs
 * the context should fail loudly, not silently no-op.
 */
export const useGuestsOptional = () => useContext(GuestsContext);

export const useGuests = () => {
  const context = useContext(GuestsContext);
  if (!context)
    throw new Error("useGuests must be used within a GuestsContext");
  return context;
};
